import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface HeroCta {
  label: string;
  href: string;
}

export interface HeroProps {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  primaryCta?: HeroCta;
  secondaryCta?: HeroCta;
  className?: string;
}

/** Shared landing hero. Router-agnostic (plain anchors) so any app can use it. */
export function Hero({ eyebrow, title, subtitle, primaryCta, secondaryCta, className }: HeroProps) {
  return (
    <section className={cn('mx-auto max-w-4xl px-6 py-24 text-center', className)}>
      {eyebrow ? (
        <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary">
          {eyebrow}
        </p>
      ) : null}
      <h1 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl">{title}</h1>
      {subtitle ? (
        <p className="mx-auto mt-5 max-w-2xl text-balance text-lg text-muted-foreground">
          {subtitle}
        </p>
      ) : null}
      {primaryCta || secondaryCta ? (
        <div className="mt-8 flex items-center justify-center gap-3">
          {primaryCta ? (
            <a
              href={primaryCta.href}
              className="inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              {primaryCta.label}
            </a>
          ) : null}
          {secondaryCta ? (
            <a
              href={secondaryCta.href}
              className="inline-flex h-11 items-center rounded-md border border-brand-sage/30 bg-card px-6 text-sm font-medium hover:bg-brand-cream-shadow"
            >
              {secondaryCta.label}
            </a>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
